import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2 } from "lucide-react";
import { sendChat } from "@/lib/api";
import { PricingCard, TransferCard, HireCard, ItineraryCard, PaymentOptionsCard } from "@/components/ActionCards";
import { BookingDialog } from "@/components/BookingDialog";
import { toast } from "sonner";

const GREETING =
  "Karibu sana — I'm Isack, your private concierge at Sadara Tour Zanzibar. Whether you dream of turquoise water and spice-scented lanes, a romantic escape, or simply a smooth ride from the airport — tell me, and I'll arrange everything.";

const ActionRenderer = ({ action, onItinerary, onBook, onPayChoice }) => {
  switch (action.type) {
    case "quote": return <PricingCard data={action.data} />;
    case "transfer_quote": return <TransferCard data={action.data} />;
    case "hire": return <HireCard data={action.data} />;
    case "itinerary": return <ItineraryCard data={action.data} onReady={onItinerary} onBook={onBook} />;
    case "payment_options": return <PaymentOptionsCard onChoose={onPayChoice} />;
    default: return null;
  }
};

export const ChatInterface = forwardRef(({ sessionId, setSessionId, initialMessage }, ref) => {
  const [messages, setMessages] = useState([{ role: "isack", text: GREETING, actions: [] }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [itinerary, setItinerary] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const scrollRef = useRef(null);
  const initSent = useRef(false);

  const scrollDown = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };
  useEffect(scrollDown, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg, actions: [] }]);
    setBusy(true);
    try {
      const res = await sendChat(sessionId, msg);
      if (!sessionId) setSessionId(res.session_id);
      setMessages((m) => [...m, { role: "isack", text: res.reply, actions: res.actions || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "isack", text: "Forgive me — I lost the connection for a moment. Could you say that again?", actions: [] }]);
    } finally {
      setBusy(false);
    }
  };

  useImperativeHandle(ref, () => ({ send }));

  useEffect(() => {
    if (initialMessage && !initSent.current) {
      initSent.current = true;
      send(initialMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage]);

  const onBook = (it) => { setItinerary(it); setBookingOpen(true); };
  const onPayChoice = () => {
    if (!itinerary) return toast.info("Let's shape your plan first, then we'll settle payment.");
    setBookingOpen(true);
  };

  return (
    <div className="flex flex-col h-full" data-testid="chat-interface">
      <div ref={scrollRef} className="flex-1 overflow-y-auto thin-scroll px-4 md:px-5 py-5 space-y-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className="max-w-[92%]">
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className={
                  m.role === "user"
                    ? "rounded-2xl rounded-tr-sm bg-gold/15 border border-gold/30 text-amber-50 px-4 py-2.5 text-sm leading-relaxed"
                    : "rounded-2xl rounded-tl-sm bg-white/[0.04] border border-white/10 text-foreground/90 px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line"
                }
                data-testid={`msg-${m.role}-${i}`}
              >
                {m.text}
              </motion.div>
              {(m.actions || []).map((a, j) => (
                <ActionRenderer key={j} action={a} onItinerary={setItinerary} onBook={onBook} onPayChoice={onPayChoice} />
              ))}
            </div>
          </div>
        ))}
        <AnimatePresence>
          {busy && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex justify-start">
              <div className="rounded-2xl rounded-tl-sm bg-white/[0.04] border border-white/10 px-4 py-3 flex items-center gap-1.5" data-testid="isack-typing">
                <span className="text-xs text-gold/70 mr-1">Isack is thinking</span>
                {[0, 1, 2].map((d) => (
                  <motion.span key={d} className="w-1.5 h-1.5 rounded-full bg-gold/70"
                    animate={{ opacity: [0.3, 1, 0.3] }} transition={{ repeat: Infinity, duration: 1, delay: d * 0.2 }} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="p-3 md:p-4 border-t border-gold/15">
        <div className="flex md:hidden gap-2 overflow-x-auto thin-scroll pb-2 mb-2 -mx-1 px-1" data-testid="mobile-suggestions">
          {[
            { t: "Airport transfer", p: "I'd like to book an airport transfer, please." },
            { t: "Something romantic", p: "I want something romantic for a couples getaway." },
            { t: "Spice Tour", p: "Tell me about the Spice Tour." },
            { t: "Safari Blue", p: "Tell me about Safari Blue." },
            { t: "Family, 5 days", p: "I have 5 days with 2 kids, what do you suggest?" },
          ].map((c) => (
            <button
              key={c.t}
              type="button"
              onClick={() => send(c.p)}
              className="shrink-0 rounded-full border border-gold/30 bg-white/5 px-3 py-1.5 text-xs text-gold/90 whitespace-nowrap hover:bg-gold/10 transition-colors"
              data-testid={`suggestion-${c.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {c.t}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 rounded-full bg-white/5 border border-gold/25 pl-4 pr-1.5 py-1.5">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Tell Isack what you're dreaming of…"
            className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground/70"
            data-testid="chat-input"
          />
          <button
            onClick={() => send()} disabled={busy}
            className="w-9 h-9 shrink-0 rounded-full bg-gold text-background flex items-center justify-center hover:bg-gold-soft transition-colors disabled:opacity-60"
            data-testid="chat-send-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <BookingDialog open={bookingOpen} onClose={() => setBookingOpen(false)} itinerary={itinerary} sessionId={sessionId} />
    </div>
  );
});

ChatInterface.displayName = "ChatInterface";
